import {
    type GetApiV1MemberClubMembersQueryError,
    type GetApiV1MemberClubMembersQueryResult,
    getGetApiV1MemberClubMembersQueryOptions,
} from '@shared/integrations/taf-api/v1/features/member/member.ts';
import type { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import useApplicationStore from '@my-pages/stores/useApplicationStore.tsx';
import { mockClubMembers } from '@my-pages/utils/backDoorUtils.ts';
import { useIsFeatureActive } from '@shared/features/feature-flags/hooks';
import { FeatureFlag } from '@shared/_legacy/api/umbraco-api';

/**
 * Custom hook for fetching club members data with optimized configuration
 * for long-running requests (up to 3 minutes response time)
 */
export const useClubMembers = (enabled?: boolean) => {
    const backDoorEnabled = useApplicationStore((state) => state.backDoorEnabled);
    const isFeatureEnabled = useIsFeatureActive(FeatureFlag.SHOW_TRUSTED_MEMBER_MEMBERS_LIST);

    // Get the base query options first
    const baseQueryOptions = getGetApiV1MemberClubMembersQueryOptions(undefined);

    // Create the enhanced query options with our custom settings
    const queryOptions = {
        ...baseQueryOptions,
        refetchOnWindowFocus: false, // Don't refetch when window gains focus
        refetchOnReconnect: false, // Don't refetch when reconnecting
        retry: 1, // Only retry once on failure
        enabled: !backDoorEnabled, // Disable query when using mock data
    };
    if (!isFeatureEnabled) queryOptions.enabled = false;
    else queryOptions.enabled = enabled ?? false;

    const query = useQuery(queryOptions) as UseQueryResult<GetApiV1MemberClubMembersQueryResult, GetApiV1MemberClubMembersQueryError> & { queryKey: QueryKey };

    // Add queryKey to the query result (following the pattern from member.ts)
    query.queryKey = queryOptions.queryKey;

    // Return mock data when backdoor is enabled, otherwise return API result
    if (backDoorEnabled) {
        return {
            ...query,
            data: mockClubMembers,
            isLoading: false,
            isError: false,
            error: null,
        };
    } else if (!isFeatureEnabled) {
        return {
            ...query,
            data: [],
            isLoading: false,
            isError: false,
            error: null,
        };
    }
    return query;
};
